import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { RetailerDetailsResponse, RetailersService, UsersService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import {firstValueFrom, Subject, takeUntil } from 'rxjs';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
  selector: 'app-manage-retailer-users',
  templateUrl: './manage-retailer-users.component.html',
  styleUrl: './manage-retailer-users.component.scss',
  host: {
    class: 'app-manage-retailer-users'
  },
  standalone: false
})
export class ManageRetailerUsersComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();
  private selectedUsersById = new Map<string, any>();

  retailerId: number = 0;
  retailer: RetailerDetailsResponse | undefined;

  filterOperator = FilterOperator;

  usersList: any[] = [];
  originalUserIds: string[] = [];

  usersTotalRecords: number = 0;
  usersRowsPerPageOption: number = 10;

  isSaving: boolean = false;

  public addUsersToRetailerFormGroup: FormGroup = this.fb.group({
    users: [[]]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UsersService,
    private retailersService: RetailersService,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        this.retailerId = Number(params['retailerId']);
        this.selectedUsersById.clear();

        if (this.retailerId > 0) {
          await this.get();
          await this.getUsers({
            pageIndex: 1,
            search: ''
          });
        }
      });
  }

  override async get(): Promise<void> {
    if (this.retailerId <= 0) {
      return;
    }

    try {
      this.retailer = await firstValueFrom(this.retailersService.apiRetailersDetailsIdGet( this.retailerId));

      this.setAssignedUsers(this.retailer.users ?? []);
    } catch (error) {
      console.error('Error fetching retailer data:', error);
    }
  }

  private setAssignedUsers(retailerUsers: any[]): void {
    retailerUsers.forEach(user => {
      if (user?.id != null) {
        this.selectedUsersById.set(user.id, user);
      }
    });

    const selectedUserIds = retailerUsers
      .filter(user => user?.id != null)
      .map(user => String(user.id));

    this.addUsersToRetailerFormGroup.controls['users'].setValue(selectedUserIds);

    this.originalUserIds = [...selectedUserIds];
  }

  onLazyLoadUsers(event: any): void {
    this.getUsers(event);
  }

  async getUsers(event: any): Promise<void> {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);

    const pageSize = Number(event?.pageSize) || this.usersRowsPerPageOption;

    this.usersRowsPerPageOption = pageSize;

    const filters: any[] = [
      {
        propertyName: 'isActive',
        operator: this.filterOperator.EQUALS,
        values: ['true']
      },
      {
        propertyName: 'role.roleActions.restrictionType',
        operator: this.filterOperator.IN,
        values: ['Retailer']
      }
    ];

    if (search) {
      filters.push({
        propertyName: 'username',
        operator: this.filterOperator.CONTAINS,
        values: [search]
      });
    }

    const filter = {
      pageNumber: activePage,
      pageSize: this.usersRowsPerPageOption,
      filters
    };

    try {
      const usersPage = await firstValueFrom(this.usersService.apiUsersFilterPost(filter));

      this.usersList = (usersPage.results ?? []).map(
        result => ({
          ...result,
          fullName: [
            result.firstName,
            result.lastName
          ].filter(Boolean)
          .join(' ')
        })
      );

      this.usersList.forEach(user => {
        if (user?.id != null) {
          this.selectedUsersById.set(user.id, user);
        }
      });

      this.usersTotalRecords = usersPage.totalCount ?? 0;
    } catch (error) {
      console.error('Error fetching retailer users:', error);

      this.usersList = [];
      this.usersTotalRecords = 0;
    }
  }

  getUsersTotalPages(): number {
    const total = Number(this.usersTotalRecords) || 0;
    const pageSize = Number(this.usersRowsPerPageOption) || 10;

    return Math.max(1,Math.ceil(total / pageSize));
  }

  public getSelectedUsers(): any[] {
    const selectedIds: string[] =
      this.addUsersToRetailerFormGroup.get('users')?.value ?? [];

    return selectedIds
      .map(id => this.selectedUsersById.get(String(id)))
      .filter(Boolean);
  }

  removeUser(user: any): void {
    const selectedUserIds: string[] =this.addUsersToRetailerFormGroup.controls['users'].value ?? [];

    const updatedUserIds =selectedUserIds.filter(id => String(id) !== String(user.id));

    this.addUsersToRetailerFormGroup.controls['users'].setValue(updatedUserIds);
  }

  async submitForm(): Promise<void> {
    if (
      this.addUsersToRetailerFormGroup.invalid ||
      this.isSaving
    ) {
      return;
    }

    if (this.retailerId <= 0) {
      return;
    }

    const selectedUserIds: string[] = [
      ...new Set<string>(
        (
          this.addUsersToRetailerFormGroup
            .get('users')
            ?.value ?? []
        ).map((id: string) => String(id))
      )
    ];

    const originalUserIds =
      this.originalUserIds.map(
        id => String(id)
      );

    const userIdsToAdd =
      selectedUserIds.filter(
        id => !originalUserIds.includes(id)
      );

    const userIdsToRemove =
      originalUserIds.filter(
        id => !selectedUserIds.includes(id)
      );

    this.isSaving = true;

    try {
      if (userIdsToRemove.length > 0) {
        const removeRequest = {
          userIds: userIdsToRemove
        };

        await firstValueFrom(this.retailersService.apiRetailersIdUsersRemovePut(this.retailerId, removeRequest));
      }

      if (userIdsToAdd.length > 0) {
        const addRequest = {
          userIds: userIdsToAdd
        };

        await firstValueFrom(this.retailersService.apiRetailersIdUsersAddPut(this.retailerId, addRequest));
      }

      await this.router.navigate(['/retailers', this.retailerId]);
    } catch (error) {
      console.error('Error updating retailer users:', error);

      await this.get();
    } finally {
      this.isSaving = false;
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
